import GroupLayer from '@arcgis/core/layers/GroupLayer';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import { WGS84_SPATIAL_REFERENCE } from './spatialReference';

export type ExternalLayerFeature = __esri.Graphic;
// rymi-TODO: we only take the first 100 features from each layer, need to handle pagination/optimization
// https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-FeatureLayer.html#querying
const MAX_FEATURES_PER_LAYER = 100;

/**
 * Queries features from ArcGIS external layers so we can display them in the UI
 * (e.g. zoom to location on map)
 */
export async function queryExternalLayerFeatures(
  layers: __esri.Layer[] | __esri.Collection<__esri.Layer>
): Promise<ExternalLayerFeature[]> {
  // Convert to a flat array of FeatureLayers
  const featureLayers = Array.from(layers)
    .flatMap((layer) => {
      if (layer instanceof GroupLayer) {
        return layer.allLayers.toArray();
      }

      return [layer];
    })
    .filter((layer) => {
      return layer instanceof FeatureLayer;
    });

  const allFeatures: ExternalLayerFeature[] = [];

  await Promise.all(
    featureLayers.map(async (layer) => {
      try {
        const renderer = layer.renderer;
        const { features } = await layer.queryFeatures({
          // Using 1=1 means “no filter", query will return all features from the layer
          where: '1=1',
          returnGeometry: true,
          outFields: ['*'],
          outSpatialReference: WGS84_SPATIAL_REFERENCE,
          num: MAX_FEATURES_PER_LAYER,
        });
        const lastEditDate = layer.editingInfo?.lastEditDate;

        features.forEach((graphic) => {
          if (graphic.geometry) {
            // rymi-TODO: make sure this is safe in next PR
            graphic.attributes.OBJECTID = `${layer.id}-${graphic.getObjectId()}`;
            // rymi-todo: doesn't look like USCG tracks last edit date at the feature level, so we're just using the layer's last edit date
            graphic.attributes.LastEditDate = lastEditDate;
            if (renderer && 'getSymbol' in renderer) {
              // @ts-expect-error: renderer.getSymbol is not typed
              graphic.symbol = renderer.getSymbol(graphic);
            }

            allFeatures.push(graphic);
          }
        });
      } catch (err) {
        console.warn(`Failed to query layer: ${layer.title}`, err);
      }
    })
  );

  return allFeatures;
}
